import type { DeliveryOutcome } from '../deliveryRun/delivery';
import type { DeliveryRatingBreakdown } from '../rating/deliveryRating';
import {
  DELIVERY_ATTEMPTS_MAX,
  PASSES_CAP,
  PERFECT_DELIVERY_RATING,
  REPLAY_PASS_COST,
} from './constants';
import { passRewardForDelivery } from './deliveryPassReward';
import type {
  DeliveryPoolEntry,
  DeliveryUpdateReason,
  PlayerEconomyState,
} from './types';
import { deliveryIdFromMission, isUrgentPoolEntry } from './types';
import type { MissionRef } from '../catalog/mission';
import {
  afterDeliverySettled,
  findPoolEntry,
  isPoolEntryDelivered,
} from './deliveryPool';
import { upsertDeliveryHistoryEntry } from './deliveryHistory';
import { unlockClaudiStickers, type ClaudiStickerId } from '../stickers/claudiStickers';
import { applyDeliveryRatingToAggregate } from './averageRating';

export type BeginDeliveryResult =
  | {
      ok: true;
      state: PlayerEconomyState;
      entry: DeliveryPoolEntry;
      isReplay: boolean;
    }
  | {
      ok: false;
      reason: 'not_in_pool' | 'locked' | 'no_passes' | 'no_attempts';
      state: PlayerEconomyState;
    };

export type SettleDeliveryInput = {
  mission: MissionRef;
  outcome: DeliveryOutcome | 'aborted';
  rating: DeliveryRatingBreakdown;
  sparksCollected?: number;
  sparksTotal?: number;
  now?: number;
};

export type SettleDeliveryResult = {
  state: PlayerEconomyState;
  updateReason: DeliveryUpdateReason;
  ratingDelta: number;
  improved: boolean;
  unlockedStickers: ClaudiStickerId[];
  urgentPassReward: number;
  deliveryPassReward: number;
};

/** Replay of a failed or previously completed delivery spends a pass. */
export function isReplayEntry(entry: DeliveryPoolEntry): boolean {
  return entry.bestRating > 0 || entry.status === 'failed';
}

export function beginDeliveryAttempt(
  state: PlayerEconomyState,
  mission: MissionRef,
  now: number,
): BeginDeliveryResult {
  const deliveryId = deliveryIdFromMission(mission);
  const entry = findPoolEntry(state, deliveryId);
  if (entry == null) {
    return { ok: false, reason: 'not_in_pool', state };
  }

  const isReplay = isReplayEntry(entry);
  if (!isReplay && entry.attemptsLeft <= 0) {
    return { ok: false, reason: 'no_attempts', state };
  }
  let passesBalance = state.passesBalance;
  if (isReplay) {
    if (passesBalance < REPLAY_PASS_COST) {
      return { ok: false, reason: 'no_passes', state };
    }
    passesBalance -= REPLAY_PASS_COST;
  }

  const nextEntry: DeliveryPoolEntry = {
    ...entry,
    updatedAt: now,
    status: 'active',
    takenOnWaitingDay:
      entry.takenOnWaitingDay ?? (isReplay ? entry.takenOnWaitingDay : entry.waitingDay),
  };
  const pool = state.deliveryPool.map((e) => (e.deliveryId === deliveryId ? nextEntry : e));

  return {
    ok: true,
    state: { ...state, passesBalance, deliveryPool: pool },
    entry: nextEntry,
    isReplay,
  };
}

export function settleDeliveryAttempt(
  state: PlayerEconomyState,
  input: SettleDeliveryInput,
): SettleDeliveryResult {
  const now = input.now ?? Date.now();
  const deliveryId = deliveryIdFromMission(input.mission);
  const entry = findPoolEntry(state, deliveryId);
  if (entry == null) {
    return {
      state,
      updateReason: 'replay_not_improved',
      ratingDelta: 0,
      improved: false,
      unlockedStickers: [],
      urgentPassReward: 0,
      deliveryPassReward: 0,
    };
  }

  // Pause → «Вернуться домой»: soft quit — not a fail, no attempt cost, no history demotion.
  if (input.outcome === 'aborted') {
    // beginDeliveryAttempt sets status to active; restore completed if this was a replay of a win.
    const restoredStatus = entry.bestRating > 0 ? 'completed' : 'active';
    const abortedEntry: DeliveryPoolEntry =
      entry.status === restoredStatus
        ? entry
        : { ...entry, status: restoredStatus, updatedAt: now };
    const pool =
      abortedEntry === entry
        ? state.deliveryPool
        : state.deliveryPool.map((e) => (e.deliveryId === deliveryId ? abortedEntry : e));
    const abortedState =
      pool === state.deliveryPool ? state : afterDeliverySettled({ ...state, deliveryPool: pool }, now);
    const stickerUnlock = unlockClaudiStickers(
      abortedState,
      {
        outcome: 'aborted',
        rating: input.rating.totalScore,
        sparksCollected: input.sparksCollected ?? 0,
        sparksTotal: input.sparksTotal ?? 0,
        isFirstSuccess: false,
        replayImproved: false,
        replayWorsened: false,
      },
      now,
    );
    return {
      state: stickerUnlock.state,
      updateReason: 'replay_not_improved',
      ratingDelta: 0,
      improved: false,
      unlockedStickers: stickerUnlock.unlockedIds,
      urgentPassReward: 0,
      deliveryPassReward: 0,
    };
  }

  if (input.outcome !== 'completed') {
    const isReplay = isReplayEntry(entry);
    // Prior success (bestRating > 0) must stay completed — a failed replay must not wipe it.
    const failedEntry: DeliveryPoolEntry = {
      ...entry,
      status: entry.bestRating > 0 ? 'completed' : 'failed',
      updatedAt: now,
      attemptsLeft: isReplay ? entry.attemptsLeft : Math.max(0, entry.attemptsLeft - 1),
    };
    const pool = state.deliveryPool.map((e) =>
      e.deliveryId === deliveryId ? failedEntry : e,
    );
    const deliveryHistory = upsertDeliveryHistoryEntry(
      state.deliveryHistory ?? [],
      input.mission,
      input.outcome,
      input.rating,
      failedEntry,
      input.sparksCollected ?? 0,
      input.sparksTotal ?? 0,
      now,
    );
    const failedState = afterDeliverySettled({ ...state, deliveryPool: pool, deliveryHistory }, now);
    const stickerUnlock = unlockClaudiStickers(
      failedState,
      {
        outcome: 'failed',
        rating: input.rating.totalScore,
        sparksCollected: input.sparksCollected ?? 0,
        sparksTotal: input.sparksTotal ?? 0,
        isFirstSuccess: false,
        replayImproved: false,
        replayWorsened: false,
      },
      now,
    );
    return {
      state: stickerUnlock.state,
      updateReason: 'replay_not_improved',
      ratingDelta: 0,
      improved: false,
      unlockedStickers: stickerUnlock.unlockedIds,
      urgentPassReward: 0,
      deliveryPassReward: 0,
    };
  }

  const newRating = input.rating.totalScore;
  const improved = newRating > entry.bestRating;
  const ratingUpdate = improved
    ? applyDeliveryRatingToAggregate(
        state.ratingSum ?? 0,
        state.ratedDeliveriesCount ?? 0,
        entry.bestRating,
        newRating,
      )
    : null;
  const ratingDelta = ratingUpdate?.ratingDelta ?? 0;

  const wasDelivered = isPoolEntryDelivered(entry);
  const nextEntry: DeliveryPoolEntry = {
    ...entry,
    bestRating: improved ? newRating : entry.bestRating,
    status: 'completed',
    updatedAt: now,
  };

  let perfectDeliveriesCount = state.perfectDeliveriesCount;
  if (
    improved &&
    newRating >= PERFECT_DELIVERY_RATING &&
    entry.bestRating < PERFECT_DELIVERY_RATING
  ) {
    perfectDeliveriesCount += 1;
  }

  const waitingDayForReward = nextEntry.takenOnWaitingDay ?? nextEntry.waitingDay;
  const passRewardAllowed = !entry.passRewardClaimed && !wasDelivered;
  const deliveryPassReward = passRewardAllowed
    ? passRewardForDelivery(nextEntry.urgency, waitingDayForReward)
    : 0;
  const pool = state.deliveryPool.map((e) =>
    e.deliveryId === deliveryId
      ? {
          ...nextEntry,
          passRewardClaimed: nextEntry.passRewardClaimed || passRewardAllowed,
          urgentRewardClaimed: nextEntry.passRewardClaimed || passRewardAllowed,
        }
      : e,
  );

  let updateReason: DeliveryUpdateReason;
  if (entry.bestRating === 0 && improved) {
    updateReason = 'new';
  } else if (improved) {
    updateReason = 'replay_improved';
  } else {
    updateReason = 'replay_not_improved';
  }

  const draftState: PlayerEconomyState = afterDeliverySettled(
    {
      ...state,
      ratingSum: ratingUpdate?.ratingSum ?? state.ratingSum ?? 0,
      ratedDeliveriesCount: ratingUpdate?.ratedDeliveriesCount ?? state.ratedDeliveriesCount ?? 0,
      totalRating: ratingUpdate?.totalRating ?? state.totalRating,
      perfectDeliveriesCount,
      urgentCompletedCount:
        (state.urgentCompletedCount ?? 0) +
        (isUrgentPoolEntry(entry) && passRewardAllowed ? 1 : 0),
      passesBalance: Math.min(PASSES_CAP, state.passesBalance + deliveryPassReward),
      deliveryPool: pool,
      deliveryHistory: upsertDeliveryHistoryEntry(
        state.deliveryHistory ?? [],
        input.mission,
        input.outcome,
        input.rating,
        nextEntry,
        input.sparksCollected ?? 0,
        input.sparksTotal ?? 0,
        now,
      ),
    },
    now,
  );
  const stickerUnlock = unlockClaudiStickers(
    draftState,
    {
      outcome: 'completed',
      rating: newRating,
      sparksCollected: input.sparksCollected ?? 0,
      sparksTotal: input.sparksTotal ?? 0,
      isFirstSuccess: updateReason === 'new',
      replayImproved: updateReason === 'replay_improved',
      replayWorsened: entry.bestRating > 0 && newRating < entry.bestRating,
    },
    now,
  );

  return {
    state: stickerUnlock.state,
    updateReason,
    ratingDelta,
    improved,
    unlockedStickers: stickerUnlock.unlockedIds,
    urgentPassReward: isUrgentPoolEntry(entry) ? deliveryPassReward : 0,
    deliveryPassReward,
  };
}

/** Восстановить попытки при отладке / тестах. */
export function resetEntryAttempts(entry: DeliveryPoolEntry): DeliveryPoolEntry {
  return { ...entry, attemptsLeft: DELIVERY_ATTEMPTS_MAX };
}
